import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  UsersIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  ChartBarIcon,
  ClockIcon,
  CalendarIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { useToast } from '../components/ToastProvider';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalMaterials: 0,
    upcomingSessions: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { notify } = useToast();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsData, activityData, profileData] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getRecentActivity(),
          apiService.getProfile().catch((err) => {
            console.error('Failed to fetch teacher profile:', err);
            return null;
          }),
        ]);

        if (statsData) setStats(statsData);
        if (activityData) setRecentActivity(activityData);
        if (profileData) setTeacherProfile(profileData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        notify('Failed to load dashboard data', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [notify]);

  const teacherName = useMemo(() => {
    if (!teacherProfile) return 'Coach';
    const profile = teacherProfile.profile || {};
    const name =
      teacherProfile.full_name ||
      profile.full_name ||
      [teacherProfile.first_name, teacherProfile.last_name].filter(Boolean).join(' ') ||
      profile.display_name ||
      teacherProfile.username;
    return name && name.trim().length > 0 ? name : 'Coach';
  }, [teacherProfile]);

  const todayMessage = useMemo(() => {
    try {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return 'Today';
    }
  }, []);

  const statCards = useMemo(
    () => [
      {
        name: 'Total Courses',
        value: stats.totalCourses,
        icon: BookOpenIcon,
        gradient: 'from-blue-500 to-blue-600',
        href: '/courses',
      },
      {
        name: 'Total Students',
        value: stats.totalStudents,
        icon: UsersIcon,
        gradient: 'from-emerald-500 to-emerald-600',
        href: '/batches',
      },
      {
        name: 'Course Materials',
        value: stats.totalMaterials,
        icon: DocumentTextIcon,
        gradient: 'from-purple-500 to-purple-600',
        href: '/materials',
      },
      {
        name: 'Upcoming Sessions',
        value: stats.upcomingSessions,
        icon: VideoCameraIcon,
        gradient: 'from-orange-500 to-orange-600',
        href: '/live-sessions',
      },
    ],
    [stats]
  );

  const quickActions = [
    {
      name: 'Create New Course',
      description: 'Add a new course to your portfolio',
      icon: BookOpenIcon,
      href: '/courses/create',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      name: 'Upload Material',
      description: 'Share learning resources with students',
      icon: DocumentTextIcon,
      href: '/materials/create',
      gradient: 'from-emerald-500 to-emerald-600',
    },
    {
      name: 'Schedule Live Session',
      description: 'Plan an interactive online class',
      icon: VideoCameraIcon,
      href: '/live-sessions/create',
      gradient: 'from-purple-500 to-purple-600',
    },
    {
      name: 'View Progress Reports',
      description: 'Check student performance analytics',
      icon: ChartBarIcon,
      href: '/progress',
      gradient: 'from-orange-500 to-orange-600',
    },
  ];

  const heroHighlights = useMemo(
    () => [
      { label: 'Courses', value: stats.totalCourses, icon: BookOpenIcon },
      { label: 'Students', value: stats.totalStudents, icon: UsersIcon },
      { label: 'Materials', value: stats.totalMaterials, icon: DocumentTextIcon },
      { label: 'Sessions', value: stats.upcomingSessions, icon: VideoCameraIcon },
    ],
    [stats]
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
        <div className="relative">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-2xl font-bold text-white shadow-lg animate-pulse">
            A
          </div>
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 opacity-30 blur animate-pulse" />
        </div>
        <div className="text-center space-y-1">
          <h3 className="text-lg font-semibold text-gray-900">Loading dashboard</h3>
          <p className="text-sm text-gray-500">Please wait while we fetch your teaching insights…</p>
        </div>
        <div className="flex space-x-2">
          <div className="h-3 w-3 animate-bounce rounded-full bg-indigo-600" />
          <div className="h-3 w-3 animate-bounce rounded-full bg-indigo-600" style={{ animationDelay: '0.12s' }} />
          <div className="h-3 w-3 animate-bounce rounded-full bg-indigo-600" style={{ animationDelay: '0.24s' }} />
        </div>
        <div className="h-2 w-64 overflow-hidden rounded-full bg-gray-200">
          <div className="h-full animate-pulse rounded-full bg-gradient-to-r from-indigo-600 to-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Banner */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 via-indigo-600 to-purple-600 text-white shadow-xl">
        <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.4),_transparent_45%)]" />
        <div className="relative p-8 lg:p-12">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-sm font-medium text-white/80">
                <SparklesIcon className="h-4 w-4" />
                {todayMessage}
              </div>
              <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                Welcome back, {teacherName}!
              </h1>
              <p className="mt-3 text-base text-white/85 sm:text-lg">
                You currently support {stats.totalStudents} student{stats.totalStudents === 1 ? '' : 's'} across {stats.totalCourses}{' '}
                course{stats.totalCourses === 1 ? '' : 's'}. Keep sharing materials and hosting live sessions to keep momentum high.
              </p>

              <div className="mt-8 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
                {heroHighlights.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-white backdrop-blur-sm"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/70">{item.label}</p>
                      <p className="text-2xl font-semibold">{item.value}</p>
                    </div>
                    <div className="rounded-xl bg-white/20 p-3 text-white/90">
                      <item.icon className="h-6 w-6" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full max-w-sm rounded-2xl bg-white/10 p-6 text-white shadow-lg backdrop-blur-sm">
              <p className="text-sm font-semibold text-white/80">Focus for today</p>
              <p className="mt-1 text-sm text-white/70">
                Keep your classroom vibrant by sharing fresh resources or scheduling a session.
              </p>

              <div className="mt-6 space-y-4">
                <Link
                  to="/materials/create"
                  className="flex items-center justify-between rounded-xl bg-white px-4 py-3 text-primary-600 shadow-sm transition hover:shadow-lg"
                >
                  <span className="text-sm font-semibold">Upload new material</span>
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <Link
                  to="/live-sessions/create"
                  className="flex items-center justify-between rounded-xl border border-white/30 px-4 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10"
                >
                  Plan live session
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Highlights */}
      <section>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((stat) => (
            <Link
              key={stat.name}
              to={stat.href}
              className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-xl"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{stat.value}</p>
                </div>
                <div className={`rounded-2xl bg-gradient-to-br ${stat.gradient} p-3 text-white shadow-lg`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs font-semibold text-primary-600">
                Manage
                <ArrowRightIcon className="h-4 w-4" />
              </div>
              <div className={`absolute inset-x-0 bottom-0 h-1.5 origin-left scale-x-0 bg-gradient-to-r ${stat.gradient} transition-transform duration-500 group-hover:scale-x-100`} />
            </Link>
          ))}
        </div>
      </section>

      {/* Content Grid */}
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <p className="text-sm text-gray-500">Track everything that has happened across your classroom.</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <SparklesIcon className="h-5 w-5" />
              </div>
            </div>

            {recentActivity.length === 0 ? (
              <div className="flex flex-col items-center justify-center space-y-4 px-6 py-12 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
                  <ClockIcon className="h-7 w-7 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">No activity yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Upload a material or schedule a session to see it appear here.
                  </p>
                </div>
                <Link
                  to="/materials/create"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700"
                >
                  Upload material
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recentActivity.map((activity) => {
                  const meta =
                    activity.type === 'course'
                      ? { icon: BookOpenIcon, badge: 'Course update', badgeClass: 'bg-blue-100 text-blue-600' }
                      : activity.type === 'material'
                      ? { icon: DocumentTextIcon, badge: 'Material upload', badgeClass: 'bg-emerald-100 text-emerald-600' }
                      : activity.type === 'session'
                      ? { icon: VideoCameraIcon, badge: 'Live session', badgeClass: 'bg-purple-100 text-purple-600' }
                      : { icon: ChartBarIcon, badge: 'Progress', badgeClass: 'bg-amber-100 text-amber-600' };

                  return (
                    <li key={activity.id} className="px-6 py-5">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-900 text-white shadow-lg">
                          <meta.icon className="h-6 w-6" />
                        </div>
                        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${meta.badgeClass}`}>
                              {meta.badge}
                            </span>
                            <p className="mt-2 text-sm font-semibold text-gray-900">{activity.description}</p>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                {quickActions.length}
              </span>
            </div>
            <div className="space-y-3 px-6 py-5">
              {quickActions.map((action) => (
                <Link
                  key={action.name}
                  to={action.href}
                  className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white/90 px-4 py-3 transition hover:border-primary-200 hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <div className={`rounded-xl bg-gradient-to-br ${action.gradient} p-2.5 text-white shadow`}>
                      <action.icon className="h-5 w-5" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">{action.name}</p>
                      <p className="text-xs text-gray-500">{action.description}</p>
                    </div>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Sessions</h2>
              <Link to="/live-sessions" className="text-sm font-semibold text-primary-600 hover:text-primary-500">
                View all
              </Link>
            </div>
            <div className="px-6 py-8 text-center">
              {stats.upcomingSessions > 0 ? (
                <>
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-100 text-orange-500">
                    <CalendarIcon className="h-8 w-8" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-gray-900">
                    {stats.upcomingSessions} upcoming session{stats.upcomingSessions === 1 ? '' : 's'}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Stay prepared—review agenda and resources ahead of time.
                  </p>
                  <Link
                    to="/live-sessions"
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-orange-600 hover:to-orange-700"
                  >
                    Manage sessions
                    <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </>
              ) : (
                <>
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-100 text-purple-500">
                    <VideoCameraIcon className="h-8 w-8" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-gray-900">No sessions scheduled</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Host a live class to keep students engaged and inspired.
                  </p>
                  <Link
                    to="/live-sessions/create"
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-purple-200 px-5 py-2 text-sm font-semibold text-purple-600 transition hover:bg-purple-50"
                  >
                    Schedule session
                    <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
